import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, X, Users } from 'lucide-react';
import { format } from 'date-fns';
// SVG is admin-uploaded trusted content
import mapService, { MapData, MapZone } from '../../services/map.service';
import { LucideIcon } from '../../utils/iconHelper';
import './InteractiveMap.css';

interface InteractiveMapProps {
  className?: string;
}

interface PopupData {
  zone: MapZone;
  x: number;
  y: number;
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({ className = '' }) => {
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [svgContent, setSvgContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [popup, setPopup] = useState<PopupData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMap();
  }, []);

  // Close popup on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node)
      ) {
        setPopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMap = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await mapService.getActiveMap();
      if (!data || !data.map) {
        setError('No hay mapa configurado');
        return;
      }
      setMapData(data);

      // Fetch SVG content
      const response = await fetch(data.map.svgUrl);
      if (!response.ok) throw new Error('Failed to load SVG');
      const text = await response.text();
      setSvgContent(text);
    } catch (err: any) {
      console.error('Error loading map:', err);
      setError('No se pudo cargar el mapa del recinto');
    } finally {
      setLoading(false);
    }
  };

  // Apply zone classes after SVG is rendered
  useEffect(() => {
    if (!svgContent || !mapData || !containerRef.current) return;

    const svgEl = containerRef.current.querySelector('svg');
    if (!svgEl) return;

    mapData.zones.forEach((zone) => {
      const el = svgEl.querySelector(`[data-zone-id="${zone.svgElementId}"]`);
      if (!el) return;

      const hasActive = zone.activeEvents && zone.activeEvents.length > 0;
      el.classList.remove('map-zone-active', 'map-zone-inactive');
      el.classList.add(hasActive ? 'map-zone-active' : 'map-zone-inactive');

      // Add click handler for active zones
      if (hasActive) {
        el.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          handleZoneClick(zone, e as MouseEvent);
        });
      }
    });

    return () => {
      // Cleanup listeners
      if (!containerRef.current) return;
      const svg = containerRef.current.querySelector('svg');
      if (!svg) return;
      mapData.zones.forEach((zone) => {
        const el = svg.querySelector(`[data-zone-id="${zone.svgElementId}"]`);
        if (el) {
          const clone = el.cloneNode(true);
          el.parentNode?.replaceChild(clone, el);
        }
      });
    };
  }, [svgContent, mapData]);

  const handleZoneClick = useCallback(
    (zone: MapZone, e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setPopup({ zone, x, y });
    },
    []
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            En curso
          </span>
        );
      case 'UPCOMING':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            Proximo
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {status}
          </span>
        );
    }
  };

  const sanitizedSvg = svgContent || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !mapData) {
    return (
      <div className="text-center py-16 text-gray-500">
        <MapPin className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="text-lg font-medium">{error || 'Mapa no disponible'}</p>
        <p className="text-sm mt-1">El mapa del recinto no esta configurado aun</p>
      </div>
    );
  }

  return (
    <div className={`interactive-map-container ${className}`} ref={containerRef}>
      {/* Render SVG */}
      <div dangerouslySetInnerHTML={{ __html: sanitizedSvg }} />

      {/* Popup */}
      <AnimatePresence>
        {popup && (
          <motion.div
            ref={popupRef}
            className="map-zone-popup"
            style={{
              left: Math.min(popup.x, (containerRef.current?.offsetWidth || 500) - 270),
              top: popup.y + 10,
            }}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-purple-600" />
                <h3 className="font-semibold text-gray-800">
                  {popup.zone.Location?.name || popup.zone.label || popup.zone.svgElementId}
                </h3>
              </div>
              <button
                onClick={() => setPopup(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {popup.zone.Location?.capacity && (
              <div className="flex items-center space-x-1 text-xs text-gray-500 mb-3">
                <Users className="w-3 h-3" />
                <span>Capacidad: {popup.zone.Location.capacity}</span>
              </div>
            )}

            {popup.zone.activeEvents && popup.zone.activeEvents.length > 0 ? (
              <div className="space-y-2">
                {popup.zone.activeEvents.map((event: any) => (
                  <div
                    key={event.id}
                    className="p-2 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-2 min-w-0">
                        {event.EventCategories?.[0]?.Category?.icon && (
                          <LucideIcon
                            name={event.EventCategories[0].Category.icon}
                            size={14}
                            className="text-purple-500 flex-shrink-0"
                          />
                        )}
                        <span className="font-medium text-sm text-gray-800 truncate">
                          {event.title}
                        </span>
                      </div>
                      {getStatusBadge(event.status)}
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                      <Clock className="w-3 h-3" />
                      <span>
                        {format(new Date(event.startTime), 'HH:mm')} -{' '}
                        {format(new Date(event.endTime), 'HH:mm')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Sin eventos activos</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InteractiveMap;
